import type { MediaImage } from '@efdigital/ll-addon-data';
import { DataTypeContent } from '@efdigital/ll-addon-data/data-types';
import { tracked } from '@glimmer/tracking';
import type { Store } from '@warp-drive/core';
import type { FindRecordOptions } from '@warp-drive/core/types';
import { findRecord } from '@warp-drive/utilities/json-api';

import { jsonApiOptions } from '../json-api.ts';
import type { MediaImagePath } from './media-image-path';
import type { MediaImageSizeType } from './models';
import { lookupSize } from './sizes.ts';

export class MediaImageInfo {
  @tracked loading = false;
  @tracked record: MediaImage | null = null;

  mediaImagePath: MediaImagePath;

  /**
   * The ID of the media-image record.
   */
  get id(): string {
    return this.mediaImagePath.id;
  }

  /**
   * The url, width and height of the media-image record for the given size.
   */
  get content() {
    if (!this.record) {
      return null;
    }

    return lookupSize(
      this.record,
      this.mediaImagePath.variant as
        | MediaImageSizeType
        | 'original'
        | 'thumbnail',
      this.mediaImagePath.size || undefined
    );
  }

  constructor(
    mediaImagePath: MediaImagePath,
    private store: Store
  ) {
    this.mediaImagePath = mediaImagePath;
    this.record = this.store.peekRecord<MediaImage>(
      DataTypeContent.MediaImage,
      mediaImagePath.id
    );

    if (!this.record) {
      this.loading = true;
      const request = findRecord<MediaImage>(
        DataTypeContent.MediaImage,
        mediaImagePath.id,
        {
          ...jsonApiOptions,
        } as FindRecordOptions
      );

      void this.store
        .request(request)
        .then((response) => {
          this.record = response.content.data;
        })
        .catch(() => {
          // Silently handle 404 errors - the image doesn't exist in our system
          // This is normal for external URLs or deleted images
          this.record = null;
        })
        .finally(() => {
          this.loading = false;
        });
    }
  }
}
